import * as React from "react";
import { cn } from "../utils";

export interface StarRatingProps extends Omit<React.HTMLAttributes<HTMLDivElement>, "onChange"> {
  value: number;
  max?: number;
  size?: "sm" | "md";
  showValue?: boolean;
  label?: string;
  interactive?: boolean;
  onChange?: (value: number) => void;
}

export function StarRating({
  value,
  max = 5,
  size = "md",
  showValue = false,
  label,
  interactive = false,
  onChange,
  className,
  ...props
}: StarRatingProps) {
  const [hoverValue, setHoverValue] = React.useState<number | null>(null);
  const clampedValue = Math.max(0, Math.min(value, max));
  const displayValue = interactive && hoverValue !== null ? hoverValue : clampedValue;
  const starSize = size === "sm" ? "h-4 w-4" : "h-5 w-5";
  const valueWidth = size === "sm" ? "w-8" : "w-10";

  const handleSelect = (val: number) => {
    if (!interactive) return;
    onChange?.(val);
  };

  return (
    <div
      className={cn("inline-flex items-center gap-3", interactive && "cursor-pointer", className)}
      {...props}
    >
      <span className="flex items-center" role={interactive ? "slider" : undefined} aria-valuenow={interactive ? displayValue : undefined} aria-valuemin={interactive ? 0 : undefined} aria-valuemax={interactive ? max : undefined}>
        {Array.from({ length: max }).map((_, i) => {
          const fill = Math.max(0, Math.min(1, displayValue - i));
          const starIndex = i + 1;
          const emptyColor = "text-[color:var(--color-text,#0f172a)]/20";
          const fillColor = "text-amber-500";

          const starSvg = (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              className={cn(starSize, emptyColor)}
              fill="currentColor"
            >
              <path d="m12 17.27 5.18 3.16-1.64-5.81L20.5 9.5l-6-.26L12 4 9.5 9.24l-6 .26 4.96 5.12-1.64 5.81z" />
            </svg>
          );

          return (
            <span
              key={`star-${i}`}
              className="relative inline-block mr-1 last:mr-0"
              onMouseEnter={() => interactive && setHoverValue(starIndex)}
              onMouseLeave={() => interactive && setHoverValue(null)}
              onClick={() => handleSelect(starIndex)}
            >
              {interactive ? (
                <button
                  type="button"
                  aria-label={`Rate ${starIndex} out of ${max}`}
                  className="relative inline-flex items-center justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/60 rounded"
                >
                  {starSvg}
                  {fill > 0 && (
                    <span
                      className={cn("absolute inset-0 overflow-hidden", fillColor)}
                      style={{ width: `${fill * 100}%` }}
                      aria-hidden
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        className={cn(starSize)}
                        fill="currentColor"
                      >
                        <path d="m12 17.27 5.18 3.16-1.64-5.81L20.5 9.5l-6-.26L12 4 9.5 9.24l-6 .26 4.96 5.12-1.64 5.81z" />
                      </svg>
                    </span>
                  )}
                </button>
              ) : (
                <>
                  {starSvg}
                  {fill > 0 && (
                    <span
                      className={cn("absolute inset-0 overflow-hidden", fillColor)}
                      style={{ width: `${fill * 100}%` }}
                      aria-hidden
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        className={cn(starSize)}
                        fill="currentColor"
                      >
                        <path d="m12 17.27 5.18 3.16-1.64-5.81L20.5 9.5l-6-.26L12 4 9.5 9.24l-6 .26 4.96 5.12-1.64 5.81z" />
                      </svg>
                    </span>
                  )}
                </>
              )}
            </span>
          );
        })}
      </span>
      {showValue && (
        <span
          className={cn("text-sm font-semibold tabular-nums text-right", valueWidth, "self-center")}
          style={{ color: "var(--color-text,#0f172a)" }}
        >
          {displayValue.toFixed(1)}
        </span>
      )}
      {label && (
        <span
          className="text-sm self-center"
          style={{ color: "var(--color-text,#0f172a)", opacity: 0.8 }}
        >
          {label}
        </span>
      )}
    </div>
  );
}
